import { createApi } from '@reduxjs/toolkit/query/react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const normalizeEmail = (email?: string): string => (email || '').trim().toLowerCase();

const toMillis = (value: unknown): number => {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      toDate?: () => Date;
      seconds?: number | string;
      nanoseconds?: number | string;
      _seconds?: number | string;
      _nanoseconds?: number | string;
      sec?: number | string;
      nsec?: number | string;
    };
    if (typeof maybeTimestamp.toDate === 'function') {
      return maybeTimestamp.toDate().getTime();
    }

    const rawSeconds =
      maybeTimestamp.seconds ??
      maybeTimestamp._seconds ??
      maybeTimestamp.sec;
    const rawNanos =
      maybeTimestamp.nanoseconds ??
      maybeTimestamp._nanoseconds ??
      maybeTimestamp.nsec;

    const seconds = Number(rawSeconds);
    const nanos = Number(rawNanos ?? 0);

    if (Number.isFinite(seconds)) {
      const safeNanos = Number.isFinite(nanos) ? nanos : 0;
      return seconds * 1000 + Math.floor(safeNanos / 1e6);
    }
  }

  return 0;
};

const getSessionSortKey = (session: unknown): number => {
  const data = session as {
    date_created?: unknown;
    created_at?: unknown;
    last_updated?: unknown;
  };
  return (
    toMillis(data.date_created) ||
    toMillis(data.created_at) ||
    toMillis(data.last_updated)
  );
};

const firebaseBaseQuery = async (args: { type: string; userEmail?: string; threadId?: string }) => {
  try {
    const { type, ...params } = args;

    switch (type) {
      case 'fetchUserSessions': {
        const { userEmail } = params;
        const normalizedUserEmail = normalizeEmail(userEmail);
        if (!normalizedUserEmail) {
          throw new Error('User email is required for sessions query');
        }

        const sessionsRef = collection(db, 'sessionsList', normalizedUserEmail, 'sessions');
        console.log(`[Sessions] Querying Firestore path: ${sessionsRef.path}`);
        const sessionsSnapshot = await getDocs(sessionsRef);
        console.log(`[Sessions] Loaded ${sessionsSnapshot.size} session docs for ${normalizedUserEmail}`);

        const sessions: unknown[] = [];
        sessionsSnapshot.forEach((docSnap) => {
          sessions.push({
            id: docSnap.id,
            ...docSnap.data()
          });
        });

        const sortedSessions = sessions.sort((a: unknown, b: unknown) => {
          return getSessionSortKey(b) - getSessionSortKey(a);
        });

        return { data: sortedSessions };
      }

      case 'fetchSessionByThreadId': {
        const { userEmail, threadId } = params;
        const normalizedUserEmail = normalizeEmail(userEmail);
        if (!normalizedUserEmail) {
          throw new Error('User email is required for session lookup');
        }
        const sessionDocRef = doc(db, 'sessionsList', normalizedUserEmail, 'sessions', threadId!);
        const sessionDoc = await getDoc(sessionDocRef);

        if (sessionDoc.exists()) {
          return {
            data: {
              id: sessionDoc.id,
              ...sessionDoc.data()
            }
          };
        } else {
          throw new Error('Session not found');
        }
      }

      default:
        throw new Error(`Unknown Firebase query type: ${type}`);
    }
  } catch (error) {
    const firebaseError = error as { code?: string; message?: string };
    return {
      error: {
        message: firebaseError?.code
          ? `${firebaseError.message} (code: ${firebaseError.code})`
          : (firebaseError?.message || 'Unknown Firestore error'),
      },
    };
  }
};

export const firebaseApiSlice = createApi({
  reducerPath: 'firebaseApi',
  baseQuery: firebaseBaseQuery as any,
  tagTypes: ['Sessions'],
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    fetchUserSessions: builder.query({
      query: ({ userEmail }: { userEmail: string }) => ({
        type: 'fetchUserSessions',
        userEmail,
      }),
      providesTags: ['Sessions'],
      keepUnusedDataFor: 300,
    }),

    fetchSessionByThreadId: builder.query({
      query: ({ userEmail, threadId }: { userEmail: string; threadId: string }) => ({
        type: 'fetchSessionByThreadId',
        userEmail,
        threadId,
      }),
      providesTags: (_result, _error, { threadId }) => [{ type: 'Sessions', id: threadId }],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useFetchUserSessionsQuery,
  useFetchSessionByThreadIdQuery,
  useLazyFetchSessionByThreadIdQuery,
} = firebaseApiSlice;
